// API Configuration
export type ApiEnvironment = "development" | "production" | "local";

export interface ApiConfig {
  baseUrl: string;
  environment: ApiEnvironment;
}

/**
 * "local" (backends on this machine) is offered only when the admin itself is
 * opened on this machine. Servers only ever offer DEV and LIVE, however they run it.
 */
export const LOCAL_ENV_AVAILABLE = ["localhost", "127.0.0.1"].includes(globalThis.location?.hostname ?? "");

// API URLs. Local = postermaker-api (also proxies /api/auth to auth-services).
const API_URLS = {
  development: "https://postermaker-dev-api.debutinfotech.in",
  production: "https://api.thepostermakers.com",
  local: "http://localhost:18001",
} as const;

// DrawPost Ads backend URLs — a separate service that owns client/brand data.
// Keyed by the same environment toggle as the core API.
const DRAWPOST_API_URLS = {
  development: "https://drawpost-dev-ads-api.debutinfotech.in",
  production: "https://api.drawpost.app",
  local: "http://localhost:18009",
} as const;

/** Environments this build can switch between, in toggle order. */
export const AVAILABLE_ENVIRONMENTS: ApiEnvironment[] = LOCAL_ENV_AVAILABLE
  ? ["development", "production", "local"]
  : ["development", "production"];

export const ENVIRONMENT_LABEL: Record<ApiEnvironment, { short: string; long: string }> = {
  development: { short: "DEV", long: "Development Server" },
  production: { short: "LIVE", long: "Production Server" },
  local: { short: "LOCAL", long: "Local machine" },
};

export const nextEnvironment = (current: ApiEnvironment): ApiEnvironment => {
  const index = AVAILABLE_ENVIRONMENTS.indexOf(current);
  return AVAILABLE_ENVIRONMENTS[(index + 1) % AVAILABLE_ENVIRONMENTS.length];
};

// Get current API configuration from localStorage or default to development
export const getApiConfig = (): ApiConfig => {
  const savedEnv = localStorage.getItem("apiEnvironment");
  const environment: ApiEnvironment = AVAILABLE_ENVIRONMENTS.includes(savedEnv as ApiEnvironment)
    ? (savedEnv as ApiEnvironment)
    : "development";

  return {
    baseUrl: API_URLS[environment],
    environment,
  };
};

// Save API environment preference
export const setApiEnvironment = (environment: ApiEnvironment): void => {
  localStorage.setItem("apiEnvironment", environment);
};

// Get full API URL for a specific endpoint
export const getApiUrl = (endpoint: string): string => {
  const config = getApiConfig();
  const normalizedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;
  return `${config.baseUrl}${normalizedEndpoint}`;
};

// Get full DrawPost Ads API URL for a specific endpoint (brand/client data lives here).
export const getDrawpostApiUrl = (endpoint: string): string => {
  const { environment } = getApiConfig();
  const normalizedEndpoint = endpoint.startsWith("/")
    ? endpoint
    : `/${endpoint}`;
  return `${DRAWPOST_API_URLS[environment]}${normalizedEndpoint}`;
};

// Export current configuration
export const apiConfig = getApiConfig();

// Export API URLs for reference
export { API_URLS };
